import { memone } from '@deqo/foundation';
import { pascalCase } from 'change-case';
import { tint } from 'polished';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { ComponentTuple, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { PointType } from '../../../concepts/objects/PointType';
import { Enum } from '../../../platform/Enum';
import { round } from '../../../platform/geom/Math';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import {
    Figure,
    Physical,
    RelativeTo,
    TransformHints,
    TransformScope,
    TransformTweak
} from '../../canvas/entities/Physical';
import { ContainerLike } from '../../canvas/entities/PhysicalMechanics';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { PreferredTextColor, TextAlignment } from '../../canvas/entities/TextConventions';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { ConnectTarget } from '../connector/ConnectTarget';
import { ShapeAlgorithm, ShapeData } from './ShapeAlgorithm';
import { ShapeContentField } from './ShapeContentField';
import { ShapeKind, ShapeStyle } from './ShapeEnums';
import { ShapeLayout } from './ShapeLayout';
import { shapePropertyLayout } from './ShapePropertyLayouts';

const mutate = defineCommand(
    'shapeMutate',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
);

const shapeDef = {
    name: 'Shape',
    implements: [
        Physical,
        Resizable,
        ContainerLike,
        GraffitiTarget,
        PreferredTextColor,
        Snappable,
        ConnectTarget,
    ],
    mutation: mutate,
    propertyLayout: shapePropertyLayout,
};

const randomCanvas = document.createElement('canvas');
const randomCtx = randomCanvas.getContext('2d')!;

const layoutAffectingProps = [
    'width', 'height',
    'background', 'content', 'cornerRadius', 'fontFamily', 'fontSize', 'fontColor', 'fontStyle', 'fontWeight',
    'kind', 'shadow', 'sides', 'stroke', 'style', 'tail', 'textMargin', 'textAlignH', 'textAlignV',
];

@component(shapeDef)
export class Shape extends RectangularPhysical implements Physical, Snappable, PreferredTextColor, ConnectTarget {

    @property({ type: 'string', default: 'white', style: true })
    public background!: string;

    @property({ type: 'boolean', default: true })
    public container!: boolean;

    @property({ type: 'string', oldName: 'content|value' })
    public content: string;

    @property({ type: 'number', default: 4, style: true })
    public cornerRadius!: number;

    @property({ type: 'string', default: 'Inter', oldName: 'content|fontFamily', style: true })
    public fontFamily!: string;

    @property({ type: 'string', oldName: 'content|fontColor', style: true })
    public fontColor!: string;

    @property({ type: 'number', default: 24, oldName: 'content|fontSize', style: true })
    public fontSize!: number;

    @property({ type: 'string', default: 'normal', oldName: 'content|fontStyle', style: true })
    public fontStyle!: string;

    @property({ type: 'number', default: 400, oldName: 'content|fontWeight', style: true })
    public fontWeight!: number;

    @property({ type: 'number', default: Enum.default(ShapeKind) })
    public kind!: ShapeKind;

    @property({ type: 'number' })
    public shadow!: number;

    @property({ type: 'number', default: 4 })
    public sides!: number;

    @property({ type: 'number', default: 2, style: true })
    public stroke!: number;

    @property({ type: 'number', default: Enum.default(ShapeStyle), style: true })
    public style!: ShapeStyle;

    @property({ type: PointType, default: Point.zero })
    public tail!: Point;

    @property({ type: 'number', default: 10 })
    public textMargin!: number;

    @property({ type: 'number', default: TextAlignment.middle, oldName: 'content|textAlignH', style: true })
    public textAlignH!: TextAlignment;

    @property({ type: 'number', default: TextAlignment.middle, oldName: 'content|textAlignH', style: true })
    public textAlignV!: TextAlignment;

    public get boundary() {
        return !this.container;
    }

    public get shouldSnap() {
        return true;
    }

    @memoUsing(...layoutAffectingProps)
    public get layout() {
        return ShapeLayout(this);
    }

    @memoUsing('layout', 'content')
    public get uiFields() {
        return [ShapeContentField(this)];
    };

    public get data(): ShapeData {
        return this.computeShape(
            this.width, this.height, this.cornerRadius, this.kind, this.stroke, this.style, this.background, this.sides, this.tail,
        );
    }

    public get minWidth() {
        switch (this.kind) {
            case ShapeKind.arrow1: return this.height;
            case ShapeKind.arrow2: return this.height;
            case ShapeKind.arrow3: return this.height;
            default: return 10;
        }
    }

    public get minHeight() {
        return 10;
    }

    //
    // Physical
    //

    // public queryTransformActions(singular: boolean): TransformAction[] {

    //     const bounds = this.bounds(RelativeTo.Model);

    //     return [
    //         {
    //             key: 'cloneLeft',
    //             position: bounds.pointAt(0, .5),
    //             orientation: 'left',
    //             command: bindCommand(cloneThenConnect, ['left']),
    //             visibility: 'always' as const,
    //             style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //         },
    //         {
    //             key: 'cloneRight',
    //             position: bounds.pointAt(1, .5),
    //             orientation: 'right',
    //             command: bindCommand(cloneThenConnect, ['right']),
    //             visibility: 'always' as const,
    //             style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //         },
    //         // {
    //         //     key: 'cloneUp',
    //         //     position: bounds.pointAt(.5, 0),
    //         //     orientation: 'up',
    //         //     command: bindCommand(cloneThenConnect, ['up']),
    //         //     visibility: 'always' as const,
    //         //     style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //         // },
    //         {
    //             key: 'cloneDown',
    //             position: bounds.pointAt(.5, 1),
    //             orientation: 'down',
    //             command: bindCommand(cloneThenConnect, ['down']),
    //             visibility: 'always' as const,
    //             style: { type: 'button', icon: 'deqo://icons/hero/plus' },
    //         },
    //     ];
    // }

    public queryTransformHints(singular: boolean): TransformHints {
        if (singular) {
            return TransformHints({
                ...TransformHints.default,
                tweaks: this.tweaks(),
            });
        }
        return TransformHints.default;
    }

    public createFigure(): Figure {
        const mt = this.transform(RelativeTo.Model);
        const base = super.createFigure();

        if (this.tweakMode() === 'steer') {
            const e = mt.apply(new Point(0, this.height * .5)).round();
            const f = mt.apply(new Point(this.width, this.height * .5)).round();
            return base.concat([e, f]);
        }
        else if (this.tweakMode() === 'tail') {
            const center = base[0].add(Point.vector(base[0], base[2]).divide(2));
            const e = center.add(this.tail);
            return base.concat([e]);
        }
        else {
            return base;
        }
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c, _d, e, f] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        if (scope.type === 'tweak') {
            if (this.tweakMode() === 'steer') {
                const sv = Point.vector(e, f); // steer vector
                const w = sv.length();
                const h = hv.length();
                const xy = e.add(sv.perp().normalize().multiply(h / 2));

                this.setTransformationResult(
                    round(xy.x, 4), // Position is origin
                    round(xy.y, 4), // Position is origin
                    round(sv.angle(), 4), // Rotation is rotation of width vector
                    round(Math.max(this.minWidth, w, 4)), // Width length of width vector
                    round(Math.max(this.minHeight, h, 4)), // Height length of height vector,
                );
            }
            else if (this.tweakMode() === 'tail') {
                const center = a.add(Point.vector(a, c).divide(2));
                this.tail = Point.vector(center, e).round(4);
            }
        }
        else {
            this.setTransformationResult(
                round(a.x, 4), // Position is origin
                round(a.y, 4), // Position is origin
                round(wv.angle(), 4), // Rotation is rotation of width vector
                round(Math.max(this.minWidth, wv.length()), 4), // Width length of width vector
                round(Math.max(this.minHeight, hv.magnitude()), 4), // Height length of height vector,
            );
        }

        return this;
    }

    public path(to?: RelativeTo | Physical): Path {
        const { data } = this;

        let outline = data.paths[0].d as Path;

        for (let i = 1; i < data.paths.length; i++) {
            outline = outline.concat((data.paths[i].d));
        }

        if (to === RelativeTo.Model) {
            outline = outline.transform(this.transform(to));
        }

        return outline;
    }

    public test(mpt: Point): boolean {

        if (!super.test(mpt)) {
            return false;
        }

        const path = this.path(RelativeTo.Model);

        if (this.kind === ShapeKind.arrow1) {
            randomCtx.lineWidth = Math.max(10, this.stroke);
            if (randomCtx.isPointInStroke(path.toPath2D(), mpt.x, mpt.y)) {
                return true;
            }

            return this.layout.content2.bounds.contains(this.toRelative(mpt));
        }
        else {
            return randomCtx.isPointInPath(path.toPath2D(), mpt.x, mpt.y);
        }
    }

    //
    // PreferredTextColor
    //

    public getPreferredTextColor(): string {
        const style = this.style;
        const fill = style === ShapeStyle.fill
            ? this.background
            : (style === ShapeStyle.stroke
                ? '#ffffff'
                : tint(.75, this.background));
        return Colors.contrastTextColorFor(fill);
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].content = '';
        return ct;
    }

    public toHumanString(): string {
        return `Shape (${pascalCase(Enum.label(ShapeKind, this.kind))}, "${this.content.slice(0, 100)}")`;
    }

    public toText() {
        return this.content;
    }

    @memone()
    // eslint-disable-next-line max-len
    private computeShape(width: number, height: number, cornerRadius: number, kind: ShapeKind, stroke: number, style: ShapeStyle, color: string, sides: number, tail: Point) {
        const alg = ShapeAlgorithm.for(kind);
        const cfg = { width, height, cornerRadius, stroke, style, color, sides, tail };
        return alg(cfg);
    }

    private tweakMode() {
        // eslint-disable-next-line max-len
        if (this.kind === ShapeKind.arrow1 || this.kind === ShapeKind.arrow2 || this.kind === ShapeKind.arrow3 || this.kind === ShapeKind.arrow4) {
            return 'steer' as const;
        }
        if (this.kind === ShapeKind.cloud) {
            return 'tail' as const;
        }
        return 'default' as const;
    }

    private tweaks(): TransformTweak[] {
        if (this.tweakMode() === 'steer') {
            return [
                { offset: 4, style: 'blob' },
                { offset: 5, style: 'blob' }
            ];
        }
        if (this.tweakMode() === 'tail') {
            return [
                { offset: 4, style: 'blob' },
            ];
        }
        return [];
    }
}
